"use strict";
// The Kit Resolver: the one canonical place that turns a stored Kit plus the
// RunnitNodeDefs a caller can see into the Kit as rendered.
//
// This is the visibility spec. Web renders both the tools library dialog and
// RunnitSingleTool through it (collapsing the two divergent kitOptions paths
// that CONTEXT.md flagged as a known defect), and IMG-2026-021's
// GET /api/v2/kits mirrors these same rules in SQL. Keep it pure and
// deps-free: no firebase, no lodash, no I/O.
Object.defineProperty(exports, "__esModule", { value: true });
exports.kitTypesOf = exports.allKitNodeDefIds = exports.initialKitTag = exports.resolveKits = exports.resolveKit = exports.deriveKitTags = exports.isKitOnSurface = exports.isKitReadableByUsers = exports.normalizeKit = exports.OTHER_KIT_TAG = exports.FEATURED_KIT_TAG = exports.DEFAULT_KIT_ICON = void 0;
const types_1 = require("./types");
/**
 * Fallback icon for a Kit whose `icon` was never set (a Kit created before the
 * field existed, or a half-authored draft). A card with no icon at all reads
 * as broken, so it is worth a default.
 */
exports.DEFAULT_KIT_ICON = 'mdi-shape-outline';
/** The Kit Tag pinned to the front of the tab row when present. */
exports.FEATURED_KIT_TAG = 'Featured';
/** The Kit Tag pinned to the end of the tab row when present. */
exports.OTHER_KIT_TAG = 'Other';
const asRecordOfTrue = (value) => {
    const out = {};
    if (!value || typeof value !== 'object')
        return out;
    for (const key of Object.keys(value)) {
        // Only truthy entries count. A `false` Kit Tag means "not tagged",
        // which must not draw a tab.
        if (value[key])
            out[key] = true;
    }
    return out;
};
const normalizeLabel = (value, index, nodeDefId) => {
    if (!value || typeof value !== 'object')
        return null;
    const raw = value;
    const label = typeof raw.label === 'string' ? raw.label : '';
    const icon = typeof raw.icon === 'string' ? raw.icon : '';
    if (!label && !icon)
        return null;
    return {
        // Heal a label authored without an id rather than dropping it. The id
        // is an opaque UI key, so a positional fallback is safe and stable
        // for a given stored array.
        id: typeof raw.id === 'string' && raw.id ? raw.id : `${nodeDefId}:${index}`,
        icon,
        label,
        value: typeof raw.value === 'number' ? raw.value : null,
        type: typeof raw.type === 'string' ? raw.type : undefined,
        tooltip: typeof raw.tooltip === 'string' ? raw.tooltip : undefined,
    };
};
const normalizeTool = (value) => {
    if (!value || typeof value !== 'object')
        return null;
    const raw = value;
    const nodeDefId = typeof raw.nodeDefId === 'string' ? raw.nodeDefId.trim() : '';
    if (!nodeDefId)
        return null;
    const labels = Array.isArray(raw.labels)
        ? raw.labels
            .map((label, index) => normalizeLabel(label, index, nodeDefId))
            .filter((label) => label !== null)
        : [];
    return { nodeDefId, tags: asRecordOfTrue(raw.tags), labels };
};
/**
 * Heal a stored Kit document into the shape the resolver expects, so a
 * backend ahead of a client (or a partially authored draft) cannot crash a
 * render. Unknown fields are dropped, not thrown on. Returns null only when
 * the document has no usable Kit Type.
 */
const normalizeKit = (value, idFallback) => {
    if (!value || typeof value !== 'object')
        return null;
    const raw = value;
    const id = (0, types_1.isKitType)(raw.id) ? raw.id : ((0, types_1.isKitType)(idFallback) ? idFallback : null);
    if (!id)
        return null;
    const tools = Array.isArray(raw.tools)
        ? raw.tools.map(normalizeTool).filter((tool) => tool !== null)
        : [];
    return {
        id,
        label: typeof raw.label === 'string' && raw.label ? raw.label : id,
        icon: typeof raw.icon === 'string' && raw.icon ? raw.icon : exports.DEFAULT_KIT_ICON,
        description: typeof raw.description === 'string' ? raw.description : '',
        sortOrder: typeof raw.sortOrder === 'number' ? raw.sortOrder : types_1.KIT_TYPES.indexOf(id),
        // STRICT. Anything other than an explicit `true` is a draft, matching
        // the Firestore rule and the non-admin query in web
        // (`where('isPublished', '==', true)`), which cannot match a document
        // where the field is missing or null. Read loosely, a half-authored Kit
        // would read as live here while being unreadable to the very users it
        // claims to be published for.
        isPublished: raw.isPublished === true,
        // `=== undefined` rather than `??`: web's webpack-4 parser lints the
        // symlinked shared dist and cannot parse nullish-coalescing.
        publishedAt: raw.publishedAt === undefined ? null : raw.publishedAt,
        // Faithful (`=== true`) rather than strict-inverse, deliberately: a Kit
        // whose `isDeleted` is missing is INVISIBLE to users (see
        // `isKitReadableByUsers`) but must still appear in Manage Kits, because
        // an admin is the only one who can repair it. Reading a missing field
        // as deleted here would hide the broken Kit from the person fixing it.
        isDeleted: raw.isDeleted === true,
        deletedAt: raw.deletedAt === undefined ? null : raw.deletedAt,
        defaultNodeDefId: typeof raw.defaultNodeDefId === 'string' && raw.defaultNodeDefId
            ? raw.defaultNodeDefId
            : null,
        surfaces: raw.surfaces && typeof raw.surfaces === 'object'
            ? asRecordOfTrue(raw.surfaces)
            : null,
        tools,
    };
};
exports.normalizeKit = normalizeKit;
/**
 * Can a non-admin user read this Kit at all?
 *
 * This is the ONE rule, stated once. It mirrors the Firestore rule
 *
 *     allow read: if (resource.data.isPublished == true
 *                     && resource.data.isDeleted == false)
 *                 || isUidAdminUser(database)
 *
 * and web's non-admin binding, which queries
 * `.where('isPublished', '==', true).where('isDeleted', '==', false)`.
 *
 * Both are strict equality on BOTH fields, and a Firestore equality filter
 * never matches a document whose field is missing or null. So a Kit missing
 * either flag is unreadable to a normal user, and every surface has to agree
 * or the API hands a plugin something the web app would refuse to show.
 *
 * Takes the RAW stored value rather than a normalized Kit, because the
 * distinction that matters here (missing vs. explicitly `false`) is exactly
 * what normalizing collapses.
 */
const isKitReadableByUsers = (raw) => {
    if (!raw || typeof raw !== 'object')
        return false;
    const value = raw;
    return value.isPublished === true && value.isDeleted === false;
};
exports.isKitReadableByUsers = isKitReadableByUsers;
/**
 * Is a Kit shown on this surface? An unset or empty `surfaces` map means
 * every surface, so a Kit authored before per-surface visibility existed
 * stays visible everywhere.
 */
const isKitOnSurface = (kit, surface) => {
    if (!surface)
        return true;
    const surfaces = kit.surfaces;
    if (!surfaces || !Object.keys(surfaces).length)
        return true;
    return !!surfaces[surface];
};
exports.isKitOnSurface = isKitOnSurface;
/**
 * Order the Kit Tag tab row: Featured first, then the rest alphabetically,
 * then Other last. Derived from the tools that actually survived resolution,
 * so a tab never renders empty.
 */
const deriveKitTags = (tools) => {
    const seen = [];
    for (const tool of tools) {
        for (const key of Object.keys(tool.tags)) {
            if (!seen.includes(key))
                seen.push(key);
        }
    }
    const middle = seen
        .filter((key) => key !== exports.FEATURED_KIT_TAG && key !== exports.OTHER_KIT_TAG)
        .sort((a, b) => a.localeCompare(b));
    const ordered = [
        ...(seen.includes(exports.FEATURED_KIT_TAG) ? [exports.FEATURED_KIT_TAG] : []),
        ...middle,
        ...(seen.includes(exports.OTHER_KIT_TAG) ? [exports.OTHER_KIT_TAG] : []),
    ];
    return ordered.map((key) => ({ key, label: key }));
};
exports.deriveKitTags = deriveKitTags;
/**
 * Resolve one stored Kit against the visible tool catalogue.
 *
 * A curated tool is dropped when its RunnitNodeDef is missing from the
 * catalogue, unpublished, soft-deleted, or blocked for this team user. Stored
 * order is preserved for everything that survives (it is the curation), and
 * the Kit Tag tab row is derived from the survivors.
 *
 * Returns null when the Kit itself is unpublished, soft-deleted, or not shown
 * on the requested surface.
 */
const resolveKit = (options) => {
    const { nodeDefs, isBlocked, surface, includeUnpublished } = options;
    const kit = (0, exports.normalizeKit)(options.kit);
    if (!kit)
        return null;
    // Checked against the RAW value, not the normalized Kit: a missing
    // `isDeleted` is unreadable to a user but is kept visible to Manage Kits,
    // so the two cannot be told apart after normalizing.
    if (!includeUnpublished && !(0, exports.isKitReadableByUsers)(options.kit))
        return null;
    if (!(0, exports.isKitOnSurface)(kit, surface))
        return null;
    // Plain objects rather than Map/Set: this leaf is consumed by surfaces
    // with varied lint/runtime baselines, so it stays to the same primitives
    // the rest of shared uses.
    const byId = {};
    for (const nodeDef of nodeDefs || []) {
        if (nodeDef && typeof nodeDef.id === 'string')
            byId[nodeDef.id] = nodeDef;
    }
    const tools = [];
    const seenNodeDefIds = {};
    for (const tool of kit.tools) {
        // A Kit listing the same tool twice would render a duplicate card and
        // a duplicate key; keep the first (curated) position.
        if (seenNodeDefIds[tool.nodeDefId])
            continue;
        const nodeDef = byId[tool.nodeDefId];
        if (!nodeDef)
            continue;
        if (!includeUnpublished && (nodeDef.isPublished === false || nodeDef.isDeleted === true))
            continue;
        if (isBlocked && isBlocked(nodeDef))
            continue;
        seenNodeDefIds[tool.nodeDefId] = true;
        tools.push({
            value: nodeDef.id,
            nodeDefId: nodeDef.id,
            text: typeof nodeDef.title === 'string' ? nodeDef.title : '',
            description: typeof nodeDef.description === 'string' ? nodeDef.description : '',
            imageUrl: nodeDef.imageUrl || null,
            avatar: nodeDef.avatar || null,
            newUntil: nodeDef.newUntil === undefined ? null : nodeDef.newUntil,
            sortOrder: typeof nodeDef.sortOrder === 'number' ? nodeDef.sortOrder : 0,
            tags: tool.tags,
            labels: tool.labels,
            nodeDef,
        });
    }
    // The stored default only counts if it survived; otherwise the Kit opens
    // on its first visible tool, which is what the old first-entry-is-default
    // constants did.
    const defaultNodeDefId = tools.some((tool) => tool.nodeDefId === kit.defaultNodeDefId)
        ? kit.defaultNodeDefId
        : (tools.length ? tools[0].nodeDefId : null);
    return {
        id: kit.id,
        label: kit.label,
        icon: kit.icon,
        description: kit.description,
        sortOrder: kit.sortOrder,
        tools,
        tags: (0, exports.deriveKitTags)(tools),
        defaultNodeDefId,
    };
};
exports.resolveKit = resolveKit;
/**
 * Resolve every stored Kit, dropping the ones that are not visible and
 * ordering the survivors by sortOrder (Kit Type declaration order breaks a
 * tie, so the row is never arbitrary).
 */
const resolveKits = (options) => {
    const resolved = [];
    for (const kit of options.kits || []) {
        const next = (0, exports.resolveKit)({ ...options, kit });
        if (next)
            resolved.push(next);
    }
    return resolved.sort((a, b) => (a.sortOrder - b.sortOrder
        || types_1.KIT_TYPES.indexOf(a.id) - types_1.KIT_TYPES.indexOf(b.id)));
};
exports.resolveKits = resolveKits;
/**
 * The initial Kit Tag tab for a Kit: Featured when present, otherwise the
 * first tab. When a tool is already selected, its own first matching tab wins
 * so the user lands on a tab that contains their tool.
 */
const initialKitTag = (kit, selectedNodeDefId) => {
    const keys = kit.tags.map((tag) => tag.key);
    if (!keys.length)
        return null;
    if (selectedNodeDefId) {
        const selected = kit.tools.find((tool) => tool.nodeDefId === selectedNodeDefId);
        if (selected) {
            if (selected.tags[exports.FEATURED_KIT_TAG] && keys.includes(exports.FEATURED_KIT_TAG))
                return exports.FEATURED_KIT_TAG;
            const match = Object.keys(selected.tags).find((key) => keys.includes(key));
            if (match)
                return match;
        }
    }
    return keys.includes(exports.FEATURED_KIT_TAG) ? exports.FEATURED_KIT_TAG : keys[0];
};
exports.initialKitTag = initialKitTag;
/**
 * Every nodeDefId curated into any Kit. Membership checks only, so duplicates
 * across Kits are expected (replaces web's getAllKitIds).
 */
const allKitNodeDefIds = (kits) => {
    const ids = [];
    for (const raw of kits || []) {
        const kit = (0, exports.normalizeKit)(raw);
        if (!kit)
            continue;
        for (const tool of kit.tools)
            ids.push(tool.nodeDefId);
    }
    return ids;
};
exports.allKitNodeDefIds = allKitNodeDefIds;
const kitTypesOf = (kits) => {
    const types = [];
    for (const raw of kits || []) {
        const kit = (0, exports.normalizeKit)(raw);
        if (kit && !types.includes(kit.id))
            types.push(kit.id);
    }
    return types;
};
exports.kitTypesOf = kitTypesOf;
